import {
  CalendarToday as CalendarTodayIcon,
  HistoryOutlined as HistoryOutlinedIcon,
  InfoOutlined as InfoOutlinedIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { IconButton, Link as TableLink } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChipWithCustomTooltip } from 'src/components/chipWithCustomTooltip/ChipWithCustomTooltip';
import { FilterList } from 'src/components/filterList';
import { CustomTableWithPagination } from 'src/components/table/CustomTableWithPagination/CustomTableWithPagination';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import MainPane from 'src/components/TwoPaneLayout/MainPane';
import SidePane from 'src/components/TwoPaneLayout/SidePane';
import TwoPaneLayout from 'src/components/TwoPaneLayout/TwoPaneLayout';
import { PATHS } from 'src/constants/paths';
import { useFilterAndSorting } from 'src/hooks';
import { useSearchFilter } from 'src/hooks/useSearchFilter';
import { IFilter, ILocationListing } from 'src/interfaces';
import { escapeSearchTerms, lang } from 'src/utils';

import { useLocationTab } from '../../hooks/useLocationTab.hook';
import { ActionsContainer } from '../../LocationListPage.styled';
import { ChipSpacer } from './LocationTab.styled';

export const LocationTab = () => {
  const {
    locations,
    isLocationTabDataLoading,
    totalCount,
    totalPages,
    activeFilters,
    handlePageChange,
    onSortChangeHandle,
    selectedSortOption,
    page,
    getParamsValue,
    rowsPerPage,
  } = useLocationTab();
  const navigate = useNavigate();
  const { handleOnChangeFilterKeyword, handleOnRemoveFilterKeyword } =
    useSearchFilter();
  const [isStoreNumberFuzzySearch, setIsStoreNumberFuzzySearch] = useState(
    !('store_number.list' in activeFilters),
  );

  const onStoreNumberSearchTypeChange = () => {
    if (isStoreNumberFuzzySearch) {
      setIsStoreNumberFuzzySearch(false);
      onChangeToExactMatch('store_number.regexp', 'store_number.list');
    } else {
      setIsStoreNumberFuzzySearch(true);
      onChangeToFuzzyMatch('store_number.list', 'store_number.regexp');
    }
  };

  const locationsWithLocationGroups = locations.filter(
    (location) => (location.location_groups?.length ?? 0) > 0,
  );

  const filters: IFilter[] = [
    {
      label: lang.locationTagsName,
      labelOverride: lang.locationTags,
      options: [],
      isLoading: false,
      fieldType: 'filterWithSearch',
      badgeOptions: {
        align: 'top',
        color: 'primary',
      },
      customElement: (
        <TableLink
          component="button"
          variant="body2"
          onClick={() => navigate(PATHS.LOCATIONS_TAGS)}>
          Manage tags
        </TableLink>
      ),
    },
    {
      labelOverride: lang.ownerOperator.ownerOperatorTags,
      label: lang.locationOwnerTagsName,
      options: [],
      isLoading: false,
      fieldType: 'filterWithSearch',
      badgeOptions: {
        align: 'top',
        color: 'success',
      },
    },
    {
      label: lang.zone,
      options: [],
      isLoading: false,
      fieldType: 'filterWithSearch',
      badgeOptions: {
        align: 'top',
        color: 'default',
      },
    },
    {
      label: lang.subZone,
      options: [],
      isLoading: false,
      fieldType: 'filterWithSearch',
      badgeOptions: {
        align: 'top',
        color: 'default',
      },
    },
    {
      labelOverride: 'Store number',
      label: 'store_number',
      isLoading: false,
      options: [],
      fieldType: 'freeSearch',
      hasExactSearch: true,
      isFuzzySearch: isStoreNumberFuzzySearch,
      onSearchTypeChange: onStoreNumberSearchTypeChange,
      filterSearchVal: getParamsValue(
        isStoreNumberFuzzySearch ? 'store_number.regexp' : 'store_number.list',
      ),
      onChangeFilterKeyword: (value: string) =>
        handleOnChangeFilterKeyword(
          value,
          isStoreNumberFuzzySearch
            ? 'store_number.regexp'
            : 'store_number.list',
        ),
      onRemoveFilterKeyword: (value: string) =>
        handleOnRemoveFilterKeyword(
          value,
          isStoreNumberFuzzySearch
            ? 'store_number.regexp'
            : 'store_number.list',
        ),
    },
  ];

  const [isFuzzySearch, setIsFuzzySearch] = useState(
    !('name.list' in activeFilters),
  );
  const {
    onChangeKeyword,
    onRemoveKeyword,
    onChangeFilter,
    onChangeParams,
    onChangeToExactMatch,
    onChangeToFuzzyMatch,
    onReset,
  } = useFilterAndSorting(filters, [], isFuzzySearch, setIsFuzzySearch);

  const onResetFilters = () => {
    onReset();
    onChangeParams('store_number.list', []);
    onChangeParams('store_number.regexp', []);
    setIsStoreNumberFuzzySearch(true);
  };

  const onSearchTypeChange = () => {
    if (isFuzzySearch) {
      onChangeToExactMatch('name.regexp', 'name.list');
      setIsFuzzySearch(false);
    } else {
      onChangeToFuzzyMatch('name.list', 'name.regexp');
      setIsFuzzySearch(true);
    }
  };

  const onHandleChangeKeyword = (value: string | string[]) => {
    if (!isFuzzySearch) {
      onChangeKeyword(value);

      return;
    }

    const escaped = escapeSearchTerms(value, getParamsValue('name.regexp'));
    // we should avoid the terms re-escaping
    onChangeParams('name.regexp', [...new Set(escaped)] as string[]);
  };

  return (
    <TwoPaneLayout>
      <SidePane>
        <FilterList
          enableSearchArea
          filters={filters}
          onChangeFilter={onChangeFilter}
          onRemoveKeyword={onRemoveKeyword}
          onChangeKeyword={onHandleChangeKeyword}
          onReset={onResetFilters}
          showReset
          hasExactSearch
          onSearchTypeChange={onSearchTypeChange}
          isFuzzySearch={isFuzzySearch}
        />
      </SidePane>

      <MainPane>
        <CustomTableWithPagination
          columns={[
            { id: 'name', label: lang.locationName },
            { id: 'store_number', label: lang.storeNumber },
            { id: 'tags', label: lang.tags },
            { id: 'actions', label: '' },
          ]}
          onSortChange={onSortChangeHandle}
          selectedSortOption={selectedSortOption}
          rows={locationsWithLocationGroups}
          loading={isLocationTabDataLoading}
          renderActions={(row: ILocationListing) => (
            <ActionsContainer>
              <CustomTooltip
                title={lang.locationDetails}
                placement="top"
                skipValidateOverflow={true}>
                <IconButton
                  aria-label="clock"
                  onClick={() =>
                    navigate({
                      pathname: PATHS.LOCATION_DETAILS(
                        row.location_code.toString(),
                      ),
                    })
                  }>
                  <InfoOutlinedIcon />
                </IconButton>
              </CustomTooltip>
              <CustomTooltip
                title={lang.locationSchedule}
                placement="top"
                skipValidateOverflow={true}>
                <IconButton
                  aria-label="calendar"
                  onClick={() =>
                    navigate(
                      {
                        pathname: PATHS.LOCATION_CALENDAR(
                          row.location_code.toString(),
                        ),
                      },
                      { state: `${row.location_code}` },
                    )
                  }>
                  <CalendarTodayIcon />
                </IconButton>
              </CustomTooltip>
              <CustomTooltip
                title={lang.locationTimings}
                placement="top"
                skipValidateOverflow={true}>
                <IconButton
                  aria-label="clock"
                  onClick={() =>
                    navigate({
                      pathname: PATHS.LOCATION_TIMINGS(
                        row.location_code.toString(),
                      ),
                    })
                  }>
                  <ScheduleIcon />
                </IconButton>
              </CustomTooltip>
              <CustomTooltip
                title={lang.menuBoardHistory}
                placement="top"
                skipValidateOverflow={true}>
                <IconButton
                  aria-label="clock"
                  onClick={() =>
                    navigate({
                      pathname: PATHS.LOCATION_TIME_MACHINE(
                        row.location_code.toString(),
                      ),
                    })
                  }>
                  <HistoryOutlinedIcon />
                </IconButton>
              </CustomTooltip>
            </ActionsContainer>
          )}
          renderTags={(row: ILocationListing) => {
            const MAX_VISIBLE = 2;
            const location_tags = row.location_tags || [];
            const hidden = location_tags.length - MAX_VISIBLE;

            return (
              <div>
                {location_tags
                  .slice(0, MAX_VISIBLE)
                  .map((tag: { name: string }, i: number) => (
                    <ChipSpacer key={i}>
                      <ChipWithCustomTooltip
                        name={lang.locationTags}
                        chipName={tag?.name}
                        tooltipTitle={tag?.name}
                        tooltipWidth={140}
                        color="primary"
                        tooltipActivationWidth={62}
                        tooltipPlacement="bottom"
                      />
                    </ChipSpacer>
                  ))}
                {hidden > 0 && (
                  <ChipSpacer>
                    <ChipWithCustomTooltip
                      name={lang.locationTags}
                      chipName={`+${hidden}`}
                      tooltipTitle={`+${hidden}`}
                      tooltipWidth={90}
                      color="primary"
                      tooltipActivationWidth={80}
                      tooltipPlacement="right"
                    />
                  </ChipSpacer>
                )}
              </div>
            );
          }}
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          rowsPerPage={rowsPerPage}
          showTableControls
          onPageChange={handlePageChange}
        />
      </MainPane>
    </TwoPaneLayout>
  );
};
